import { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, ShieldCheck, BookOpen } from 'lucide-react';

export interface ParsedInsight {
  fact: string;
  context: string;
  source: string;
  final_source_url?: string;
  final_source_name?: string;
  final_source_priority?: string;
}

/**
 * Parse perplexity insights from unknown Json data into a typed array.
 * Handles both old format (source string) and new format (final_source_url, final_source_name, final_source_priority).
 */
export const parseInsightsFromJson = (data: unknown): ParsedInsight[] => {
  if (!data) return [];
  let parsed = data;
  if (typeof data === 'string') {
    try { parsed = JSON.parse(data); } catch { return []; }
  }
  if (parsed && typeof parsed === 'object' && 'insights' in parsed) {
    const insights = (parsed as { insights: unknown }).insights;
    if (Array.isArray(insights)) {
      return insights.map(item => {
        const i = item as Record<string, unknown>;
        return {
          fact: String(i.fact || ''),
          context: String(i.context || ''),
          source: String(i.source || ''),
          final_source_url: i.final_source_url ? String(i.final_source_url) : undefined,
          final_source_name: i.final_source_name ? String(i.final_source_name) : undefined,
          final_source_priority: i.final_source_priority ? String(i.final_source_priority) : undefined,
        };
      });
    }
  }
  return [];
};

const getPriorityBadge = (priority?: string) => {
  if (!priority) return null;
  const p = priority.toUpperCase();
  if (p === 'P1' || p === 'P2') {
    return {
      label: 'Znakomite źródło',
      Icon: ShieldCheck,
      className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
    };
  }
  return {
    label: 'Publikacja',
    Icon: BookOpen,
    className: 'bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30',
  };
};

interface InsightItemDisplayProps {
  insight: ParsedInsight;
  /** Resolved citation URL from old format (fallback when final_source_url is absent) */
  fallbackCitationUrl?: string | null;
  /** Compact mode for tighter layouts */
  compact?: boolean;
}

/**
 * Shared display component for a single insight (ciekawostka).
 * Shows: priority badge, fact, expandable context, source link.
 */
export const InsightItemDisplay = ({ insight, fallbackCitationUrl, compact }: InsightItemDisplayProps) => {
  const [contextOpen, setContextOpen] = useState(false);

  const badge = getPriorityBadge(insight.final_source_priority);
  const sourceUrl = insight.final_source_url || fallbackCitationUrl || undefined;
  const sourceName = insight.final_source_name || insight.source;

  return (
    <div className="space-y-2">
      {/* Priority badge */}
      {badge && (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.className}`}>
          <badge.Icon className="w-3.5 h-3.5" />
          {badge.label}
        </span>
      )}

      {/* Fact */}
      <p className={`${compact ? 'text-sm' : 'text-sm'} font-semibold text-foreground leading-relaxed`}>
        {insight.fact}
      </p>

      {/* Expandable context */}
      {insight.context && (
        <div>
          <button
            onClick={(e) => { e.stopPropagation(); setContextOpen(!contextOpen); }}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {contextOpen ? (
              <>
                <ChevronUp className="w-3.5 h-3.5" />
                Ukryj kontekst
              </>
            ) : (
              <>
                <ChevronDown className="w-3.5 h-3.5" />
                Dlaczego to ważne?
              </>
            )}
          </button>
          {contextOpen && (
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed pl-3 border-l-2 border-emerald-500/20">
              {insight.context}
            </p>
          )}
        </div>
      )}

      {/* Source link */}
      {sourceUrl && sourceName && (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-green-600 dark:text-green-400 hover:underline flex items-center gap-1 mt-1"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{sourceName}</span>
        </a>
      )}
    </div>
  );
};
